import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  Absence,
  AbsenceBody,
  AgendaEvents,
  Calendar,
  CalendarBody,
  Commitment,
  CommitmentBody,
  TicketCloseBody,
} from '@erp/shared';
import { withCommitment } from './commitment-done';

const AGENDA_KEY = ['erp', 'agenda'] as const;
/**
 * spec 039, adendo 4: o "feito" escreve no ticket (o apontamento, e o
 * encerramento no "concluir") — a tela do ticket, se estiver no cache, relê.
 */
const TICKETS_KEY = ['erp', 'tickets'] as const;

// ── Agendas ─────────────────────────────────────────────────────────────────

export function useCreateCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CalendarBody) => {
      const res = await api<Calendar>('/agenda/calendars', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useUpdateCalendar(calendarId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CalendarBody) => {
      const res = await api<Calendar>(`/agenda/calendars/${calendarId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useDeleteCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (calendarId: number) => {
      const res = await api<void>(`/agenda/calendars/${calendarId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

// ── Compromissos ────────────────────────────────────────────────────────────

export function useCreateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CommitmentBody) => {
      const res = await api<Commitment>('/agenda/commitments', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useUpdateCommitment(commitmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CommitmentBody) => {
      const res = await api<Commitment>(`/agenda/commitments/${commitmentId}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useDeleteCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commitmentId: number) => {
      const res = await api<void>(`/agenda/commitments/${commitmentId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

const EVENTS_KEY = ['erp', 'agenda', 'events'] as const;

/**
 * PATCH done com update OTIMISTA sobre todos os caches de eventos carregados.
 * Adendo 4 da 039: a API devolve o compromisso com o apontamento que o "feito"
 * gerou (`doneAppointmentId`) — ele entra no cache assim que chega, e é por ele
 * (e não pelo otimista) que a tela avisa o que aconteceu.
 */
export function useToggleCommitmentDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, done }: { id: number; done: boolean }) => {
      const res = await api<Commitment>(`/agenda/commitments/${id}/done`, {
        method: 'PATCH',
        body: { done },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onMutate: async ({ id, done }) => {
      await qc.cancelQueries({ queryKey: EVENTS_KEY });
      const previous = qc.getQueriesData<AgendaEvents>({ queryKey: EVENTS_KEY });
      qc.setQueriesData<AgendaEvents>({ queryKey: EVENTS_KEY }, (old) =>
        old
          ? {
              ...old,
              commitments: old.commitments.map((c) => (c.id === id ? { ...c, done } : c)),
            }
          : old
      );
      return { previous };
    },
    onSuccess: (saved) => {
      // a API anterior (204, durante o rollout) não devolve o compromisso
      if (saved) qc.setQueriesData<AgendaEvents>({ queryKey: EVENTS_KEY }, (old) => withCommitment(old, saved));
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.previous ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: AGENDA_KEY });
      void qc.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

/**
 * "Concluir e encerrar o ticket" (adendo 4 da 039): o "feito" com o apontamento
 * e o encerramento, numa operação só no servidor. Sem otimista: é uma ação
 * confirmada, e a tela espera a resposta para dizer o que aconteceu.
 */
export function useConcludeCommitment() {
  const qc = useQueryClient();
  return useMutation({
    // spec 054: o motivo de fechamento vai junto (a mesa com motivo o exige)
    mutationFn: async ({ id, close }: { id: number; close: TicketCloseBody }) => {
      const res = await api<Commitment>(`/agenda/commitments/${id}/conclude`, { method: 'POST', body: close });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (saved) => {
      qc.setQueriesData<AgendaEvents>({ queryKey: EVENTS_KEY }, (old) => withCommitment(old, saved));
      void qc.invalidateQueries({ queryKey: AGENDA_KEY });
      void qc.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

// ── Ausências ───────────────────────────────────────────────────────────────

export function useCreateAbsence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AbsenceBody) => {
      const res = await api<Absence>('/agenda/absences', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useUpdateAbsence(absenceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AbsenceBody) => {
      const res = await api<Absence>(`/agenda/absences/${absenceId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}

export function useDeleteAbsence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (absenceId: number) => {
      const res = await api<void>(`/agenda/absences/${absenceId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AGENDA_KEY }),
  });
}
